import { useEffect, useRef } from "react";
import { clarity } from "react-microsoft-clarity";

import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps } from "../utils/environment";
import { getBuildNumber } from "../utils/nativeUtils/customActions";

import { useUserContext } from "../hooks/useUserContext";
import { useAppContext } from "../hooks/useAppContext";
import { useDiaryContext } from "../hooks/useDiaryContext";

declare global {
  interface Window {
    newrelic: {
      setCustomAttribute: (name: string, value: string) => void;
      noticeError: (
        error: Error,
        customAttributes: Record<string, unknown>
      ) => void;
    };
  }
}

export const useTelemetry = () => {
  const user = useUserContext().user;
  const { venueGroupConfig } = useAppContext();
  const { venueConfig } = useDiaryContext();

  const userAttributesAdded = useRef<boolean>(false);
  const venueGroupAttributesAdded = useRef<boolean>(false);
  const venueAttributesAdded = useRef<boolean>(false);
  const appAttributesAdded = useRef<boolean>(false);
  const clarityInitialized = useRef<boolean>(false);
  const viewportAttributesAdded = useRef<boolean>(false);

  useEffect(() => {
    if (!user?._id || !window.newrelic || userAttributesAdded.current) return;

    userAttributesAdded.current = true;

    try {
      window.newrelic.setCustomAttribute("User-ID", user._id);

      const userName = [user.first_name, user.last_name]
        .filter(Boolean)
        .join(" ");
      if (userName) {
        window.newrelic.setCustomAttribute("User-Name", userName);
      }

      if (user.email) {
        window.newrelic.setCustomAttribute("User-Email", user.email);
      }
    } catch (error) {
      console.error("Failed to add user attributes to New Relic:", error);
    }
  }, [user]);

  useEffect(() => {
    if (
      !venueGroupConfig?._id ||
      !window.newrelic ||
      venueGroupAttributesAdded.current
    )
      return;

    venueGroupAttributesAdded.current = true;

    try {
      window.newrelic.setCustomAttribute(
        "Venue-Group-ID",
        venueGroupConfig._id
      );

      if (venueGroupConfig.title) {
        window.newrelic.setCustomAttribute(
          "Venue-Group-Name",
          venueGroupConfig.title
        );
      }
    } catch (error) {
      console.error(
        "Failed to add venue group attributes to New Relic:",
        error
      );
    }
  }, [venueGroupConfig]);

  useEffect(() => {
    if (!venueConfig?._id || !window.newrelic || venueAttributesAdded.current)
      return;

    venueAttributesAdded.current = true;

    try {
      window.newrelic.setCustomAttribute("Venue-ID", venueConfig._id);

      if (venueConfig.title) {
        window.newrelic.setCustomAttribute("Venue-Name", venueConfig.title);
      }
    } catch (error) {
      console.error("Failed to add venue attributes to New Relic:", error);
    }
  }, [venueConfig]);

  useEffect(() => {
    if (!window.newrelic || appAttributesAdded.current) return;

    appAttributesAdded.current = true;

    const addAppAttributes = async () => {
      try {
        const token = await dmnIdentity.getAccessToken();
        const environmentVars = getEnvironmentProps(token);
        const buildNumber = await getBuildNumber();

        window.newrelic.setCustomAttribute(
          "App-Version",
          `${buildNumber ?? "unknown"}`
        );

        window.newrelic.setCustomAttribute(
          "App-Build-Number",
          process.env.BUILD_BUILDNUMBER ?? "development"
        );

        window.newrelic.setCustomAttribute(
          "App-Environment",
          `${environmentVars?.ENVIRONMENT}`
        );
      } catch (error) {
        console.error("Failed to add app attributes to New Relic:", error);
      }
    };

    addAppAttributes();
  }, []);

  useEffect(() => {
    if (!window.newrelic) return;

    const updateViewportAttributes = () => {
      try {
        const width = window.innerWidth;
        const height = window.innerHeight;

        window.newrelic.setCustomAttribute("Viewport-Width", String(width));
        window.newrelic.setCustomAttribute("Viewport-Height", String(height));
        window.newrelic.setCustomAttribute(
          "Viewport-Size",
          `${width}x${height}`
        );

        // Add viewport category for easier filtering
        let viewportCategory = "desktop";
        if (width < 768) {
          viewportCategory = "mobile";
        } else if (width < 1024) {
          viewportCategory = "tablet";
        }
        window.newrelic.setCustomAttribute(
          "Viewport-Category",
          viewportCategory
        );

        viewportAttributesAdded.current = true;
      } catch (error) {
        console.error("Failed to add viewport attributes to New Relic:", error);
      }
    };

    // Set initial viewport attributes
    if (!viewportAttributesAdded.current) {
      updateViewportAttributes();
    }

    // Listen for viewport changes
    const handleResize = () => {
      updateViewportAttributes();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!user?._id || clarity.hasStarted() || clarityInitialized.current)
      return;

    clarityInitialized.current = true;

    const initializeClarity = async () => {
      try {
        const token = await dmnIdentity.getAccessToken();
        const env = getEnvironmentProps(token);

        if (!env?.REACT_APP_CLARITY_TOKEN) {
          console.error("Clarity token is missing");
          return;
        }

        clarity.init(env.REACT_APP_CLARITY_TOKEN);
        clarity.identify(String(user._id), {});
        clarity.consent();
      } catch (error) {
        console.error("Failed to initialize Clarity:", error);
      }
    };

    initializeClarity();
  }, [user]);
};
